import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ARGUS_DEFAULT_LOCALE, ARGUS_SUPPORTED_LOCALES, normalizeLocale, translate } from '../i18n'

const STORAGE_KEY = 'argus-language'
const supportedCodes = new Set(ARGUS_SUPPORTED_LOCALES.map((locale) => locale.code))

function storedLanguage() {
  if (typeof window === 'undefined') return ARGUS_DEFAULT_LOCALE
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return supportedCodes.has(stored) ? stored : ARGUS_DEFAULT_LOCALE
}

const LanguageContext = createContext({
  language: ARGUS_DEFAULT_LOCALE,
  setLanguage: () => {},
  t: (key, variables) => translate(ARGUS_DEFAULT_LOCALE, key, variables),
  supportedLocales: ARGUS_SUPPORTED_LOCALES,
})

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(storedLanguage)

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = language
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, language)
  }, [language])

  function setLanguage(nextLanguage) {
    const normalized = normalizeLocale(nextLanguage)
    if (!supportedCodes.has(normalized)) return
    setLanguageState(normalized)
  }

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key, variables) => translate(language, key, variables),
      supportedLocales: ARGUS_SUPPORTED_LOCALES,
    }),
    [language],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  return useContext(LanguageContext)
}
